"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import { type BlogPost } from "@/lib/blog";
import { Calendar, Clock, ArrowRight, ChevronRight, Bookmark, Filter, Search, Sparkles } from "lucide-react";
import { Header, Footer } from "@/components/landing";
import { CSSReveal, StaggerContainer, StaggerItem } from "@/components/motion";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";

const BLOG_HERO_IMAGE = "https://images.unsplash.com/photo-1522199755839-a2bacb67c546?q=80&w=2672&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";
const BLOG_ARCHIVE_BG = "https://images.unsplash.com/photo-1643722997021-7c1c5a5a1159?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";

const PLATFORM_CONFIG: Record<string, { color: string; label: string }> = {
  tiktok: { color: "bg-[#000000] border-[#EE1D52]", label: "TikTok" },
  instagram: { color: "bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] border-white/20", label: "Instagram" },
  twitter: { color: "bg-black border-white/20", label: "X / Twitter" },
  linkedin: { color: "bg-[#0077b5] border-white/20", label: "LinkedIn" },
  youtube: { color: "bg-[#FF0000] border-white/20", label: "YouTube" },
  reddit: { color: "bg-[#FF4500] border-white/20", label: "Reddit" },
};

function PlatformRibbon({ tags }: { tags: string[] }) {
  const platform = tags.find(tag => PLATFORM_CONFIG[tag.toLowerCase()]);
  if (!platform) return null;

  const config = PLATFORM_CONFIG[platform.toLowerCase()];

  return (
    <div className="absolute top-0 left-0 w-32 h-32 overflow-hidden pointer-events-none z-20">
      <div className={cn(
        "absolute top-6 -left-8 w-40 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-center text-white transform -rotate-45 shadow-lg border-b",
        config.color
      )}>
        {config.label}
      </div>
    </div>
  );
}

function BlogCard({ post }: { post: BlogPost }) {
  return (
    <Link
      href={`/blog/${post.slug}`}
      className="group flex flex-col h-full bg-surface-solid/40 backdrop-blur-xl border border-border-light rounded-[2rem] overflow-hidden hover:border-brand-1/50 hover:shadow-[0_0_40px_-10px_rgba(91,91,255,0.4)] transition-all duration-500 hover:-translate-y-2 relative"
    >
      <div className="relative aspect-[16/11] overflow-hidden">
        {/* Platform Ribbon */}
        <PlatformRibbon tags={post.tags} />

        {post.image ? (
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-brand-1/20 via-brand-3/20 to-brand-2/20 animate-pulse-glow" />
        )}

        {/* Glass Overlay on Hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
          <span className="text-white text-xs font-bold flex items-center gap-2 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
            Read Full Insight <ArrowRight className="w-4 h-4" />
          </span>
        </div>

        {/* Category Badge (Standard) */}
        {!post.tags.some(t => PLATFORM_CONFIG[t.toLowerCase()]) && (
          <div className="absolute top-4 left-4 z-10">
            <span className="px-3 py-1 text-[9px] font-black bg-white/10 backdrop-blur-md text-white border border-white/20 rounded-full uppercase tracking-[0.15em]">
              {post.tags[0] || "Article"}
            </span>
          </div>
        )}
      </div>

      <div className="p-8 flex flex-col flex-1 relative">
        {/* Subtle Decorative Element */}
        <div className="absolute top-0 right-8 w-12 h-1 bg-gradient-to-r from-brand-1 to-transparent opacity-30 group-hover:w-24 transition-all duration-700" />

        <div className="flex items-center gap-4 text-[10px] text-text-muted mb-5 font-mono uppercase tracking-[0.2em]">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3 text-brand-1" />
            <span>{new Date(post.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
          </div>
          <span className="w-1 h-1 rounded-full bg-border-strong/40" />
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-brand-2" />
            <span>{post.readingTime}</span>
          </div>
        </div>

        <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-brand-1 transition-colors leading-tight line-clamp-2">
          {post.title}
        </h3>

        <p className="text-text-secondary text-base mb-8 line-clamp-3 flex-1 leading-relaxed opacity-70 group-hover:opacity-100 transition-opacity">
          {post.description}
        </p>

        <div className="flex items-center justify-between mt-auto pt-6 border-t border-white/5">
          <div className="flex items-center gap-3">
             <div className={cn(
               "w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black text-white border border-white/10 shadow-lg",
               post.author === 'Tavlo Team' ? 'bg-blue-600 shadow-blue-500/20' : 'bg-brand-gradient shadow-brand-1/20'
             )}>
               {post.author === 'Tavlo Team' ? 'T' : post.author.split(' ').map(n => n[0]).join('')}
             </div>
             <span className="text-xs font-bold text-text-primary">{post.author}</span>
          </div>
          <div className="w-10 h-10 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white group-hover:bg-brand-1 group-hover:border-brand-1 group-hover:shadow-[0_0_20px_rgba(91,91,255,0.4)] transition-all duration-500">
            <ChevronRight className="w-5 h-5" />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function BlogClient({ initialPosts }: { initialPosts: BlogPost[] }) {
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const categories = useMemo(() => {
    const tags = new Set<string>();
    tags.add("All");
    initialPosts.forEach(post => {
      post.tags.forEach(tag => {
        if (["Productivity", "Curation", "Workflow", "Social Media", "Second Brain", "Learning"].includes(tag) || PLATFORM_CONFIG[tag.toLowerCase()]) {
          tags.add(tag);
        }
      });
    });
    if (tags.size < 4) {
      initialPosts.forEach(post => post.tags.forEach(tag => tags.add(tag)));
    }
    return Array.from(tags).slice(0, 10);
  }, [initialPosts]);

  const filteredPosts = useMemo(() => {
    return initialPosts.filter(post => {
      const matchesFilter = activeFilter === "All" || post.tags.some(tag => tag.toLowerCase() === activeFilter.toLowerCase());
      const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           post.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [initialPosts, activeFilter, searchQuery]);

  const featuredPost = initialPosts[0];

  return (
    <div className="min-h-screen bg-black text-text-body font-sans selection:bg-brand-1/20 selection:text-text-primary overflow-hidden relative pb-20">
      {/* Persistent Archive Background */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <img
          src={BLOG_ARCHIVE_BG}
          alt=""
          className="w-full h-full object-cover opacity-80 scale-105 blur-[8px]"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/90" />
      </div>

      <Header />

      <main className="relative z-10">
        <section className="relative h-[85vh] min-h-[700px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={BLOG_HERO_IMAGE}
              alt="Tavlo Blog Background"
              className="w-full h-full object-cover opacity-50 scale-105 animate-float-slow"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/40 to-black" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,black_85%)]" />
          </div>

          <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
            <CSSReveal>
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-brand-1 text-[10px] font-black uppercase tracking-[0.3em] mb-10 backdrop-blur-xl shadow-2xl">
                <span className="w-2 h-2 rounded-full bg-brand-1 animate-pulse-glow shadow-[0_0_10px_rgba(91,91,255,1)]" />
                The Knowledge System
              </div>
            </CSSReveal>

            <CSSReveal delay={0.1}>
              <h1 className="text-6xl sm:text-8xl md:text-9xl font-black tracking-[ -0.05em] mb-10 text-white">
                THE <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-1 via-brand-3 to-brand-2 italic">TAVLO</span> BLOG
              </h1>
            </CSSReveal>

            <CSSReveal delay={0.2}>
              <p className="text-2xl sm:text-3xl text-white/60 max-w-4xl mx-auto leading-relaxed font-light italic mb-12">
                "Curation is the art of giving the future a better past."
              </p>
            </CSSReveal>

            <CSSReveal delay={0.4}>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md">
                   <Sparkles className="w-4 h-4 text-brand-3" />
                   <span className="text-white font-bold text-sm tracking-wide">100+ Readers</span>
                </div>
                <div className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md">
                   <Bookmark className="w-4 h-4 text-brand-2" />
                   <span className="text-white font-bold text-sm tracking-wide">50+ Insights</span>
                </div>
              </div>
            </CSSReveal>
          </div>

          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-40">
            <div className="w-1 h-12 rounded-full bg-gradient-to-b from-brand-1 to-transparent" />
          </div>
        </section>

        <section className="sticky top-20 z-50 bg-black/80 backdrop-blur-2xl border-y border-white/5 py-6 px-4 mb-20">
           <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex items-center gap-4 overflow-x-auto no-scrollbar pb-2 md:pb-0 w-full md:w-auto">
                 <div className="flex items-center gap-2 text-text-muted mr-4 border-r border-white/10 pr-6 shrink-0">
                    <Filter className="w-4 h-4" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Filter</span>
                 </div>
                 {categories.map((category) => (
                   <button
                     key={category}
                     onClick={() => setActiveFilter(category)}
                     className={cn(
                       "px-5 py-2 rounded-full text-xs font-bold transition-all duration-300 whitespace-nowrap border tracking-wide",
                       activeFilter === category
                        ? "bg-brand-1 border-brand-1 text-white shadow-[0_0_20px_rgba(91,91,255,0.4)]"
                        : "bg-white/5 border-white/10 text-text-secondary hover:text-white hover:border-white/20"
                     )}
                   >
                     {category}
                   </button>
                 ))}
              </div>

              <div className="relative w-full md:w-64">
                 <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                 <input
                   type="text"
                   value={searchQuery}
                   onChange={(e) => setSearchQuery(e.target.value)}
                   placeholder="Search insights..."
                   className="w-full bg-white/5 border border-white/10 rounded-2xl py-2.5 pl-12 pr-4 text-sm text-white focus:outline-none focus:border-brand-1 transition-all placeholder:text-text-muted/50 font-medium"
                 />
              </div>
           </div>
        </section>

        {activeFilter === "All" && !searchQuery && featuredPost && (
          <section className="px-4 pb-32">
            <div className="max-w-6xl mx-auto">
              <CSSReveal>
                <Link
                  href={`/blog/${featuredPost.slug}`}
                  className="group relative flex flex-col lg:flex-row bg-surface-solid/40 backdrop-blur-2xl border border-white/5 rounded-[3rem] overflow-hidden hover:border-brand-1/40 transition-all duration-700 shadow-[0_30px_100px_-20px_rgba(0,0,0,0.5)]"
                >
                  <div className="lg:w-3/5 relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                    <PlatformRibbon tags={featuredPost.tags} />
                    {featuredPost.image ? (
                      <img
                        src={featuredPost.image}
                        alt={featuredPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[2000ms] ease-out"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-brand-1/20 to-brand-3/20" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />

                    <div className="absolute bottom-8 left-8">
                      <div className="flex items-center gap-3 px-4 py-2 rounded-2xl bg-brand-1 text-white text-[10px] font-black uppercase tracking-[0.2em] shadow-2xl">
                        <Sparkles className="w-3 h-3 fill-current" />
                        Featured Masterclass
                      </div>
                    </div>
                  </div>

                  <div className="lg:w-2/5 p-12 flex flex-col justify-center relative">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-brand-1/5 rounded-full blur-[100px] pointer-events-none" />

                    <div className="flex items-center gap-4 text-xs text-text-muted mb-8 font-mono uppercase tracking-[0.3em]">
                      <span className="text-brand-1 font-black">{new Date(featuredPost.date).toLocaleDateString("en-US", { month: "long", day: "numeric" })}</span>
                      <span className="w-1 h-1 rounded-full bg-white/20" />
                      <span>{featuredPost.readingTime}</span>
                    </div>

                    <h2 className="text-4xl sm:text-5xl font-black text-white mb-8 group-hover:text-brand-1 transition-colors leading-[1.1] tracking-tight">
                      {featuredPost.title}
                    </h2>

                    <p className="text-text-secondary text-lg mb-12 line-clamp-4 leading-relaxed opacity-80">
                      {featuredPost.description}
                    </p>

                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white text-lg font-black shadow-xl shadow-blue-500/20 border border-white/10">
                          T
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-black text-white uppercase tracking-wider">Tavlo Team</span>
                          <span className="text-[10px] text-brand-3 font-bold uppercase tracking-[0.2em]">Curation Experts</span>
                        </div>
                      </div>

                      <div className="w-14 h-14 rounded-full bg-white text-black flex items-center justify-center group-hover:bg-brand-1 group-hover:text-white transition-all duration-500 shadow-xl group-hover:shadow-brand-1/40">
                        <ArrowRight className="w-6 h-6" />
                      </div>
                    </div>
                  </div>
                </Link>
              </CSSReveal>
            </div>
          </section>
        )}

        <section className="py-20 px-6 relative">
          <div className="max-w-6xl mx-auto relative z-10">
            <CSSReveal>
              <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
                <div>
                  <h2 className="text-5xl font-black text-white mb-6">THE <span className="text-text-muted">ARCHIVE</span></h2>
                  <p className="text-text-secondary text-lg max-w-xl leading-relaxed font-light italic opacity-70">
                    Deep dives into the systems and philosophies of modern curation. Search. Organize. Replay.
                  </p>
                </div>
              </div>
            </CSSReveal>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeFilter + searchQuery}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                {filteredPosts.length === 0 ? (
                  <div className="text-center py-32 border border-dashed border-white/10 rounded-[3rem] bg-white/5 backdrop-blur-sm">
                    <Sparkles className="w-12 h-12 text-brand-1 mx-auto mb-6 opacity-30" />
                    <p className="text-text-secondary text-xl font-light italic">
                      No matching insights found. Try exploring another system.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
                    {filteredPosts.map((post) => (
                      <BlogCard key={post.slug} post={post} />
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <CSSReveal delay={0.3}>
              <div className="mt-40 p-12 md:p-20 rounded-[4rem] bg-gradient-to-br from-brand-1/20 via-black to-brand-3/10 border border-brand-1/20 relative overflow-hidden group shadow-[0_50px_100px_-30px_rgba(91,91,255,0.2)]">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-1/10 rounded-full blur-[150px] -mr-64 -mt-64 group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-16">
                  <div className="text-center lg:text-left max-w-2xl">
                    <h3 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight">UPGRADE YOUR <span className="italic text-brand-3">MIND</span>.</h3>
                    <p className="text-xl text-text-secondary leading-relaxed font-light italic opacity-80">
                      Join our private newsletter and receive the latest curation frameworks, tool reviews, and productivity deep-dives. Delivered once a month. No noise.
                    </p>
                  </div>
                  <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-4">
                    <input
                      type="email"
                      placeholder="system@knowledge.com"
                      className="px-8 py-5 rounded-[2rem] bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-brand-1 transition-all min-w-[320px] font-bold text-lg backdrop-blur-xl"
                    />
                    <button className="px-10 py-5 rounded-[2rem] bg-white text-black font-black text-lg hover:bg-brand-1 hover:text-white transition-all shadow-2xl hover:shadow-brand-1/40 uppercase tracking-widest">
                      Subscribe
                    </button>
                  </div>
                </div>
              </div>
            </CSSReveal>
          </div>
        </section>
      </main>

      <div className="relative z-10 bg-black mt-20 border-t border-white/5">
        <Footer />
      </div>

      <div className="noise-overlay" />
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-[100] border-[40px] border-black/5" />
    </div>
  );
}
